import { useState, useEffect, useRef } from "react";

/**
 * Custom hook for pagination functionality
 * Manages page state, stored filters, and handles refetching when page changes
 * @param {Function} fetchFunction - Function to fetch data, accepts (filters, pageNum)
 * @param {number} pageSize - Number of items per page (default: 10)
 * @returns {object} - { page, setPage, storedFilters, hasSearched, handleSearch, handleReset }
 */
export const usePagination = (fetchFunction, pageSize = 10) => {
  const [page, setPage] = useState(1);
  const [storedFilters, setStoredFilters] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  
  // Use ref to store the latest fetchFunction to avoid dependency issues
  const fetchFunctionRef = useRef(fetchFunction);
  
  useEffect(() => {
    fetchFunctionRef.current = fetchFunction;
  }, [fetchFunction]);

  // Handle page change - refetch with stored filters
  // Only trigger when page changes, not when storedFilters or hasSearched change
  useEffect(() => {
    if (storedFilters && hasSearched && page >= 1) {
      // Only refetch if page changed (not initial search)
      fetchFunctionRef.current(storedFilters, page);
    }
  }, [page]); // Only depend on page

  /**
   * Handle search submission
   * Stores filters, resets page to 1, sets hasSearched to true, and triggers initial fetch
   * @param {object} filters - Filter data to store and use for fetching
   */
  const handleSearch = (filters) => {
    setStoredFilters(filters);
    setHasSearched(true);
    setPage(1); // Reset to first page when new search
    // Trigger initial fetch directly
    if (fetchFunctionRef.current) {
      fetchFunctionRef.current(filters, 1);
    }
  };

  /**
   * Reset pagination state
   * Clears stored filters, resets page to 1, and sets hasSearched to false
   */
  const handleReset = () => {
    setStoredFilters(null);
    setPage(1);
    setHasSearched(false);
  };

  return {
    page,
    setPage,
    storedFilters,
    hasSearched,
    handleSearch,
    handleReset,
  };
};

